<script setup lang="ts">
  import type { StrapiRegistrationData } from '@nuxtjs/strapi'
  import { notify } from 'notiwind'
  import type { SpontaneousType } from '@profilpublic/api-contract/career-site'
  import { SPONTANEOUS_TYPE_LABELS } from '@profilpublic/api-contract/career-site'
  import type {
    ApplicationCreateData,
    ApplyAccountPayload,
    ApplyCareerSiteFragment,
    ApplyDocuments,
    ApplyEmployerFragment,
    ApplyJobFragment,
    ApplyProfile,
    ApplyQuestionnaireFragment,
    CandidateDepositData,
    CvExtraction,
    MotivationQuestionKey
  } from '~/types/apply'
  import { buildMotivationAnswers, extractOriginFromQuery } from '~/utils/application'
  import { resolveMotivationQuestions } from '~/utils/employer'
  import { handleError } from '~/utils/error'

  const props = withDefaults(
    defineProps<{
      job?: ApplyJobFragment | null
      employer?: ApplyEmployerFragment
      careerSite?: ApplyCareerSiteFragment
      mobility?: boolean
      mode?: 'page' | 'modal' // 'page' (job) | 'modal' (career)
      spontaneousType?: SpontaneousType | null
      /** Save a candidate profile instead of creating an application. */
      resumeDeposit?: boolean
    }>(),
    { job: null, employer: null, careerSite: null, mode: 'page', mobility: false, spontaneousType: null, resumeDeposit: false }
  )

  const emit = defineEmits<{
    applied: [profile?: ApplyProfile | null]
    close: []
  }>()

  const client = useStrapiClient()
  const auth = useStrapiAuth()
  const { query } = useRoute()
  const { user, fetchUser } = useUser()
  const { trackApplication } = useAnalytics()
  const spontaneous = computed(() => !!props.careerSite && !props.job)
  const spontaneousTypeLabel = computed(() => (spontaneous.value ? SPONTANEOUS_TYPE_LABELS[props.spontaneousType || 'employment'] : undefined))

  const spontaneousQuestionnaire = ref<ApplyQuestionnaireFragment>(null)
  onMounted(async () => {
    if (!spontaneous.value) return
    try {
      const res = await client<{ data: ApplyQuestionnaireFragment }>(
        `/career-sites/${props.careerSite?.id}/spontaneous-questionnaire/${props.spontaneousType || 'employment'}`
      )
      spontaneousQuestionnaire.value = res.data
    } catch (error) {
      handleError(error)
    }
  })

  // Career sites use custom primary/text colors; page mode keeps the brand blues.
  const theme = computed(() => ({
    primary: props.careerSite?.primaryColor || null,
    text: props.careerSite?.textColor || null
  }))
  provideCareerTheme(theme)

  const themeStyle = computed(() => {
    if (!theme.value.primary) return undefined
    return {
      color: theme.value.text || undefined,
      '--apply-primary': theme.value.primary,
      '--apply-text': theme.value.text || undefined,
      '--focus-color': theme.value.primary,
      '--focus-ring-color': `${theme.value.primary}80`
    }
  })

  const Steps = { APPLICATION: 'application', PROFILE: 'profile', ACCOUNT: 'account' } as const
  type StepKey = (typeof Steps)[keyof typeof Steps]
  const stepperSteps = computed(() => [
    { key: Steps.APPLICATION, label: props.resumeDeposit ? 'Mes documents' : 'Ma candidature' },
    { key: Steps.PROFILE, label: 'Mon profil' },
    { key: Steps.ACCOUNT, label: 'Mon compte' }
  ])

  const step = ref<StepKey>(Steps.APPLICATION)
  const analysing = ref(false)
  const documents = ref<ApplyDocuments | null>(null)
  const extraction = ref<CvExtraction | null>(null) // normalized output of POST /ml/cv-extract (null = manual fill)

  // Step 1 "Suivant": run the AI pre-fill, then show the profile form.
  const onDocumentsNext = async (values: ApplyDocuments) => {
    documents.value = values
    step.value = Steps.PROFILE
    analysing.value = true
    try {
      const formData = new FormData()
      formData.append('cv', values.cv)
      if (values.message) formData.append('coverLetterText', values.message)
      extraction.value = await client<CvExtraction>('/ml/cv-extract', { method: 'POST', body: formData })
    } catch (error) {
      console.error('[ApplyFlow] CV extraction failed:', error)
      notify({
        group: 'main',
        type: 'danger',
        title: 'L’analyse du CV a échoué',
        text: 'Vos informations n’ont pas pu être extraites. Vous pouvez continuer et remplir le profil manuellement.'
      })
      extraction.value = null
    } finally {
      analysing.value = false
    }
  }

  // Step 1 "Remplir manuellement": skip the AI, open the empty profile form.
  const onDocumentsManual = (values: ApplyDocuments) => {
    documents.value = values
    extraction.value = null
    step.value = Steps.PROFILE
  }

  const profile = ref<ApplyProfile | null>(null)

  const submitting = ref(false)

  const motivationQuestions = computed(() => resolveMotivationQuestions(props.job?.employer))
  const motivationKey = (index: number) => `motivationQuestion${index + 1}` as MotivationQuestionKey

  const generatedLetterRef = ref<{ getGeneratedLetterPdf: () => Promise<File | null> } | null>(null)
  const letterFromQuestions = computed(() => {
    const d = documents.value
    if (!d) return false
    return motivationQuestions.value.some((_, index) => d[motivationKey(index)])
  })

  const letterItems = computed(() =>
    motivationQuestions.value.map((question, index) => ({ question, answer: documents.value?.[motivationKey(index)] || '' }))
  )

  const buildFormData = (emailOverride?: string, letterOverride?: File | null) => {
    const d: Partial<ApplyDocuments> = documents.value || {}
    const p: Partial<ApplyProfile> = profile.value || {}
    const formData = new FormData()
    if (d.cv) formData.append('files.cv', d.cv)
    const letter = d.letter || letterOverride
    if (letter) formData.append('files.letter', letter)
    if (d.letter && letterOverride) formData.append('files.attachedFiles', letterOverride)
    if (d.otherFile) formData.append('files.otherFile', d.otherFile)
    if (p.avatar) formData.append('files.avatar', p.avatar)
    if (d.attachedFiles?.length) {
      for (const file of d.attachedFiles) formData.append('files.attachedFiles', file)
    }

    const data: ApplicationCreateData = {
      gender: p.gender,
      firstName: p.firstName,
      lastName: p.lastName,
      email: emailOverride || p.email,
      phone: p.phone,
      linkedin: p.linkedin,
      location: p.location,
      summary: p.summary,
      official: p.official,
      experience: p.experience,
      exam: p.exam,
      sectors: p.sectors,
      subSectors: p.subSectors,
      expertises: p.expertises,
      drivingLicenses: p.drivingLicenses,
      languages: p.languages,
      internalCandidate: p.internalCandidate,
      rqth: p.rqth,
      message: d.message
    }
    const motivationAnswers = buildMotivationAnswers(d, motivationQuestions.value)
    if (motivationAnswers) data.motivationAnswers = motivationAnswers
    if (d.questionnaireAnswers?.length) data.questionnaireAnswers = d.questionnaireAnswers
    if (props.job) {
      data.job = props.job.id
      if (query.o) {
        data.origin = extractOriginFromQuery(query)
      }
      const originUrl = sessionStorage.getItem(`job:${props.job.id}`)
      if (originUrl) data.originUrl = originUrl
    }
    if (props.careerSite?.id) {
      data.mobility = props.mobility
    }
    if (spontaneous.value) {
      data.searchedSector = d.searchedSector
      data.spontaneousType = props.spontaneousType || 'employment'
    }
    formData.append('data', JSON.stringify(data))
    return formData
  }

  const submitApplication = async (payload: ApplyAccountPayload) => {
    let generatedLetter: File | null = null
    if (letterFromQuestions.value) {
      await nextTick()
      generatedLetter = await generatedLetterRef.value?.getGeneratedLetterPdf()
    }
    const endpoint = props.careerSite
      ? `career-sites/${props.careerSite.id}/apply${props.job ? `/${props.job.id}` : ''}`
      : payload.newAccount
        ? `/applications/apply/${props.job?.id}`
        : '/applications'
    await client(endpoint, { method: 'POST', body: buildFormData(payload.email, generatedLetter) })
    trackApplication()
    notify({ group: 'main', type: 'success', title: 'Candidature envoyée', text: 'Votre candidature a bien été transmise.' })
    emit('applied')
  }

  // Shared candidate payload for authenticated and anonymous deposits.
  const buildCandidateData = (emailOverride?: string): CandidateDepositData => {
    const d: Partial<ApplyDocuments> = documents.value || {}
    const p: Partial<ApplyProfile> = profile.value || {}
    const existingIncognitoEmployer = (user.value as { candidate?: { incognitoEmployer?: number | { id?: number } | null } | null } | null)?.candidate
      ?.incognitoEmployer
    const existingIncognitoEmployerId: number | undefined =
      typeof existingIncognitoEmployer === 'number' ? existingIncognitoEmployer : existingIncognitoEmployer?.id
    return {
      gender: p.gender,
      firstName: p.firstName,
      lastName: p.lastName,
      email: emailOverride || p.email,
      phone: p.phone,
      linkedin: p.linkedin,
      location: p.location,
      summary: p.summary,
      official: p.official,
      // Exams only apply to declared public servants.
      exam: p.official === true ? p.exam : null,
      experience: p.experience,
      sectors: p.sectors,
      subSectors: p.subSectors,
      expertises: p.expertises,
      drivingLicenses: p.drivingLicenses,
      languages: p.languages,
      rqth: p.rqth,
      // Preserve the existing value unless this field was edited in the flow.
      incognitoEmployer:
        !d.incognitoEmployerTouched && existingIncognitoEmployerId !== undefined ? existingIncognitoEmployerId : (d.incognitoEmployer ?? null),
      jobSearchStatus: 'active'
    }
  }

  const submitResume = async (payload: ApplyAccountPayload) => {
    const currentUser = user.value as { id?: number; candidate?: { id?: number } | null } | null

    // Employer accounts cannot deposit a candidate CV.
    if (currentUser && !currentUser.candidate?.id) {
      notify({ group: 'main', type: 'danger', title: 'Accès refusé', text: 'Cette fonctionnalité est réservée aux candidats.' })
      return
    }

    const d: Partial<ApplyDocuments> = documents.value || {}
    const p: Partial<ApplyProfile> = profile.value || {}
    const email = payload.email || p.email

    if (currentUser) {
      // Update the user first, then the candidate profile.
      const userFormData = new FormData()
      if (p.avatar) userFormData.append('files.avatar', p.avatar)
      userFormData.append('data', JSON.stringify({ firstName: p.firstName, lastName: p.lastName, phone: p.phone }))
      await client(`/users/${currentUser.id}`, { method: 'PUT', body: userFormData })

      // Keep the existing visibility setting for authenticated candidates.
      const candidateFormData = new FormData()
      if (d.cv) candidateFormData.append('files.cv', d.cv)
      if (d.letter) candidateFormData.append('files.letter', d.letter)
      if (d.otherFile) candidateFormData.append('files.otherFile', d.otherFile)
      candidateFormData.append('data', JSON.stringify(buildCandidateData(email)))
      await client(`/candidates/${currentUser.candidate.id}`, { method: 'PUT', body: candidateFormData })

      await fetchUser()
      notify({
        group: 'main',
        type: 'success',
        title: 'CV déposé',
        text: 'Vous serez contacté si une opportunité correspond à votre profil.'
      })
    } else {
      // Register the account, candidate profile and files in one request.
      const registerFormData = new FormData()
      const registration = {
        username: email,
        email,
        password: payload.password,
        firstName: p.firstName,
        lastName: p.lastName,
        phone: p.phone,
        newsletter: !!payload.newsletter
      }
      Object.entries(registration).forEach(([key, value]) => registerFormData.append(key, String(value ?? '')))
      // The single consent controls both newsletter and CV library visibility.
      registerFormData.append('candidateData', JSON.stringify({ ...buildCandidateData(email), publicVisible: !!payload.newsletter }))
      if (d.cv) registerFormData.append('files.cv', d.cv)
      if (d.letter) registerFormData.append('files.letter', d.letter)
      if (d.otherFile) registerFormData.append('files.otherFile', d.otherFile)
      if (p.avatar) registerFormData.append('files.avatar', p.avatar)
      // The module type only describes JSON registration, not multipart data.
      await auth.register(registerFormData as unknown as StrapiRegistrationData)

      notify({
        group: 'main',
        type: 'success',
        title: 'CV déposé',
        text: `Confirmez votre e-mail (envoyé à ${email}) puis connectez-vous pour accéder à votre profil.`
      })
    }

    emit('applied', profile.value)
  }

  const onAccountSubmit = async (payload: ApplyAccountPayload = {}) => {
    if (submitting.value) return
    submitting.value = true
    try {
      if (props.resumeDeposit) await submitResume(payload)
      else await submitApplication(payload)
    } catch (error) {
      const e = error.error || error
      const known = {
        'Already applied': spontaneous.value ? 'Vous avez déjà envoyé une candidature spontanée' : 'Vous avez déjà postulé à cette offre',
        'Email or Username are already taken': 'Cet email a déjà un compte - connectez-vous.'
      }
      handleError(error, known[e.message] || e.message || true)
    } finally {
      submitting.value = false
    }
  }

  const onProfileSubmit = (values: ApplyProfile) => {
    profile.value = values
    if (props.careerSite || user.value) return onAccountSubmit()
    step.value = Steps.ACCOUNT
  }
</script>

<template>
  <div id="apply-flow" :class="mode === 'page' ? 'pb-24 pt-6' : ''" :style="themeStyle">
    <div :class="{ 'px-4': mode === 'page' }">
      <ApplyStepDocuments
        v-if="step === Steps.APPLICATION"
        :job="job"
        :questionnaire="job?.questionnaire ?? spontaneousQuestionnaire"
        :resume-deposit="resumeDeposit"
        :spontaneous="spontaneous"
        :spontaneous-type-label="spontaneousTypeLabel"
        @next="onDocumentsNext"
        @manual="onDocumentsManual"
      />

      <template v-else-if="step === Steps.PROFILE">
        <ApplyAnalysing v-if="analysing" :title="resumeDeposit ? 'Je dépose mon CV' : 'Je postule'" />
        <ApplyStepProfile
          v-else
          :extraction="extraction"
          :documents="documents"
          :job="job"
          :career-site="careerSite"
          :submitting="submitting"
          :resume-deposit="resumeDeposit"
          @submit="onProfileSubmit"
          @back="step = Steps.APPLICATION"
        />
      </template>

      <ApplyStepAccount
        v-else-if="step === Steps.ACCOUNT"
        :profile="profile"
        :submitting="submitting"
        :resume-deposit="resumeDeposit"
        @submit="onAccountSubmit"
        @back="step = Steps.PROFILE"
      />
    </div>

    <!-- Hidden: builds the cover-letter PDF from the questionnaire answers at submission time. -->
    <FormGeneratedLetter
      v-if="letterFromQuestions && profile"
      ref="generatedLetterRef"
      :candidate-first-name="profile.firstName || ''"
      :candidate-last-name="profile.lastName || ''"
      :speaker="job?.speaker"
      :employer-name="job?.employer?.name || employer?.name || ''"
      :items="letterItems"
    />

    <div class="fixed inset-x-0 bottom-0 z-10" v-if="mode === 'page'">
      <ApplyStepper :steps="stepperSteps" :current="step" />
    </div>
  </div>
</template>

<style>
  /* Override global viking form accents (assets/css/tailwind.css) when a career theme is set. */
  #apply-flow .form-input,
  #apply-flow .form-select,
  #apply-flow .form-file,
  #apply-flow .form-file::file-selector-button {
    color: var(--apply-text, inherit);
  }

  #apply-flow .form-input:focus,
  #apply-flow .form-select:focus {
    border-color: var(--focus-color, #58d9db);
  }

  #apply-flow .form-input:focus-visible,
  #apply-flow .form-select:focus-visible {
    --tw-ring-color: var(--focus-ring-color, rgb(88 217 219 / 0.5));
  }

  #apply-flow .form-file:focus {
    border-color: var(--focus-color, #58d9db);
    --tw-ring-color: var(--focus-ring-color, rgb(88 217 219 / 0.5));
  }

  #apply-flow .form-checkbox,
  #apply-flow .form-radio {
    color: var(--focus-color, #58d9db);
  }

  #apply-flow .form-checkbox:focus-visible,
  #apply-flow .form-radio:focus-visible {
    --tw-ring-color: var(--focus-ring-color, rgb(88 217 219 / 0.5));
  }

  #apply-flow button[role='switch'][aria-checked='true'] {
    background-color: var(--focus-color, #58d9db) !important;
  }
</style>
